import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Typography,
    IconButton,
    TextField,
    Button,
    Stack,
    CircularProgress,
    Alert
} from '@mui/material';
import { CloseIcon, ContentCopyIcon } from 'icons/materialIcons';
import { useTeam } from 'team/contexts/teamContext';
import { createTeamApiKey } from 'team/apis/teamApi';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';

const TeamCreateApiKeyModal = ({ open, onClose, onSuccess }) => {
    const { currentTeamId } = useTeam();
    const [label, setLabel] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [createError, setCreateError] = useState('');
    const [newKeyData, setNewKeyData] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Reset state when modal opens
    useEffect(() => {
        if (open) {
            setLabel('');
            setCreateError('');
            setNewKeyData(null);
            setIsSubmitting(false);
        }
    }, [open]);

    const handleCreateSubmit = async () => {
        if (!label.trim()) return;
        setIsSubmitting(true);
        setCreateError('');
        try {
            const data = await createTeamApiKey(currentTeamId, { label });
            if (data && data.success) {
                setNewKeyData(data.apiKey);
                if (onSuccess) onSuccess();
            } else {
                setCreateError(data?.message || 'Failed to create API key');
            }
        } catch (error) {
            console.error('Create API Key Error:', error);
            setCreateError(error.message || 'An error occurred');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCopyKey = () => {
        if (newKeyData?.secretKey) {
            const copyText = newKeyData.secretKey;

            // Primary method: navigator.clipboard
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(copyText).then(() => {
                    setSnackbar({
                        open: true,
                        message: 'API Key copied to clipboard!',
                        severity: 'success'
                    });
                }).catch((err) => {
                    console.error('Clipboard write failed:', err);
                    fallbackCopy(copyText);
                });
            } else {
                fallbackCopy(copyText);
            }
        }
    };

    const fallbackCopy = (text) => {
        try {
            const textArea = document.createElement("textarea");
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand("copy");
            document.body.removeChild(textArea);
            setSnackbar({
                open: true,
                message: 'API Key copied to clipboard!',
                severity: 'success'
            });
        } catch (err) {
            console.error('Fallback copy failed:', err);
            setSnackbar({
                open: true,
                message: 'Failed to copy key. Please select and copy manually.',
                severity: 'error'
            });
        }
    };

    const handleClose = () => {
        onClose();
    };

    const primaryButtonStyles = {
        padding: '10px 32px',
        textTransform: 'none',
        borderRadius: '26px',
        fontWeight: '600',
        backgroundColor: '#000',
        color: '#fff',
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#333',
            boxShadow: 'none'
        },
        '&.Mui-disabled': {
            backgroundColor: '#e0e0e0',
            color: '#a6a6a6'
        }
    };

    const secondaryButtonStyles = {
        padding: '10px 32px',
        textTransform: 'none',
        borderRadius: '26px',
        fontWeight: '600',
        backgroundColor: '#f5f5f5',
        color: '#757575',
        border: '1px solid #e0e0e0',
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#eeeeee',
            boxShadow: 'none'
        }
    };

    const handleCopyAndClose = () => {
        handleCopyKey();
        setTimeout(() => {
            onClose();
        }, 500);
    };

    return (
        <>
            <Modal
                open={open}
                onClose={onClose}
                aria-labelledby="create-api-key-modal"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(4px)',
                    bgcolor: 'rgba(0, 0, 0, 0.4)',
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: 500,
                        bgcolor: '#ffffff',
                        borderRadius: 3,
                        boxShadow: 24,
                        p: 4
                    }}
                >
                    <IconButton
                        onClick={onClose}
                        sx={{ position: 'absolute', top: 16, right: 16, color: '#9e9e9e' }}
                        disabled={isSubmitting}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        gutterBottom
                        sx={{ color: '#212121' }}
                    >
                        {newKeyData ? 'Copy your API Key' : 'Give your API Key a label'}
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{ mb: 3, color: '#757575' }}
                    >
                        {newKeyData ? 'You will only see this API Key once' : 'Labels help identify recently used API keys'}
                    </Typography>

                    {newKeyData ? (
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#424242', fontSize: '0.85rem' }}>
                                API Key
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                bgcolor: '#f9f9f9',
                                p: 1.5,
                                borderRadius: 1,
                                border: 'none'
                            }}>
                                <Typography sx={{
                                    fontFamily: 'monospace',
                                    flexGrow: 1,
                                    wordBreak: 'break-all',
                                    fontSize: '0.9rem',
                                    color: '#333',
                                    fontWeight: 'bold'
                                }}>
                                    {newKeyData.secretKey}
                                </Typography>
                                <IconButton onClick={handleCopyKey} size="small" sx={{ ml: 1, color: '#828282' }}>
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ mb: 4 }}>
                            {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#333', fontSize: '0.85rem' }}>
                                API Key Label
                            </Typography>
                            <TextField
                                fullWidth
                                autoFocus
                                variant="outlined"
                                placeholder="API Key Label"
                                value={label}
                                onChange={(e) => setLabel(e.target.value)}
                                disabled={isSubmitting}
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        backgroundColor: '#f9f9f9',
                                        '& fieldset': { border: 'none' },
                                    }
                                }}
                            />
                        </Box>
                    )}

                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        {!newKeyData && (
                            <Button
                                onClick={onClose}
                                disabled={isSubmitting}
                                sx={secondaryButtonStyles}
                            >
                                Cancel
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            onClick={newKeyData ? handleCopyAndClose : handleCreateSubmit}
                            disabled={!newKeyData && (!label.trim() || isSubmitting)}
                            startIcon={newKeyData ? <ContentCopyIcon /> : null}
                            sx={{
                                ...primaryButtonStyles,
                                px: 4,
                                height: '38px',
                                fontSize: '0.85rem',
                            }}
                        >
                            {isSubmitting ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                newKeyData ? 'Copy & Close' : 'Next'
                            )}
                        </Button>
                    </Stack>
                </Box>
            </Modal>

            <TeamNotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                handleClose={() => setSnackbar({ ...snackbar, open: false })}
            />
        </>
    );
};

export default TeamCreateApiKeyModal;
